import React from 'react';
import { motion } from 'framer-motion';
import {
  Sparkles,
  Zap,
  Cpu,
  Bot,
  Star,
  ArrowRight,
  TrendingUp,
  ShieldCheck,
  ArrowDownToLine,
  Activity,
  Award,
} from 'lucide-react';
import type { UserProfileResponse } from '@nova-mine/shared';
import { formatCoinAmount } from '@nova-mine/shared';
import { NovaCoin3D } from '../components/common/NovaCoin3D.js';
import { AnimatedCoinCounter } from '../components/common/AnimatedCoinCounter.js';
import { GlassCard } from '../components/common/GlassCard.js';
import { GradientCard } from '../components/common/GradientCard.js';
import { AnimatedButton } from '../components/common/AnimatedButton.js';
import { MinerStatusBadge } from '../components/common/MinerStatusBadge.js';
import { MiningReactor } from '../components/common/MiningReactor.js';

interface HomeScreenProps {
  profile: UserProfileResponse | null;
  liveAccrued: number;
  isClaiming: boolean;
  onClaimAll: () => void;
  onNavigate: (tab: 'main' | 'auto' | 'stars' | 'referrals' | 'activity' | 'profile') => void;
}

export const HomeScreen: React.FC<HomeScreenProps> = ({
  profile,
  liveAccrued,
  isClaiming,
  onClaimAll,
  onNavigate,
}) => {
  const balance = profile?.balance || 0;
  const totalDailyRate = profile?.mining.aggregate.total_daily_rate || 2000;
  const totalRatePerSec = profile?.mining.aggregate.total_rate_per_second || 0;

  const mainActive = profile?.mining.main.active ?? true;
  const autoUnlocked = profile?.mining.auto.unlocked ?? false;
  const starsUnits = profile?.mining.stars.total_units || 0;

  const totalMinersCount = (mainActive ? 1 : 0) + (autoUnlocked ? 1 : 0) + starsUnits;
  const canClaim = liveAccrued >= 0.0001;

  // Authoritative lifetime earnings calculation
  const lifetimeEarned = balance + (profile?.referrals.total_reward || 0);

  // Approximate cycle progress for visual indicator
  const now = Date.now();
  const lastClaim = profile?.mining.main.last_claimed_at || now;
  const elapsedSec = Math.min(86400, Math.max(0, (now - lastClaim) / 1000));
  const cyclePercent = Math.min(100, Math.round((elapsedSec / 86400) * 100));

  return (
    <div className="w-full space-y-5 animate-fade-in pb-4">
      {/* 1. VIP Balance Hero Section */}
      <GlassCard variant="gold" className="border-amber-500/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-[11px] font-black uppercase tracking-widest text-amber-200/90 flex items-center space-x-1.5">
              <span>Account Coin Balance</span>
            </span>
            <ShieldCheck className="w-3.5 h-3.5 text-nova-gold" />
          </div>
          <span className="text-[10px] font-mono font-extrabold px-2.5 py-0.5 rounded-full bg-amber-500/20 text-nova-gold border border-amber-500/35 shadow-sm">
            Spendable Vault
          </span>
        </div>

        <div className="mt-3.5 flex items-baseline space-x-3">
          <NovaCoin3D size="lg" animate={true} />
          <div className="flex items-baseline space-x-1.5">
            <span className="text-3xl sm:text-4xl font-black font-mono tracking-tight text-gold-gradient">
              {formatCoinAmount(balance, 2)}
            </span>
            <span className="text-xs font-black text-amber-300 uppercase tracking-wider">
              NOVA
            </span>
          </div>
        </div>

        {/* Lifetime Earnings & Hashrate Sub-Grid */}
        <div className="mt-5 pt-3.5 border-t border-white/10 grid grid-cols-2 gap-3 text-xs">
          <div className="bg-black/25 rounded-2xl p-2.5 border border-white/5">
            <span className="text-gray-400 text-[10px] uppercase font-bold tracking-wider block">
              Lifetime Earned
            </span>
            <span className="font-mono font-black text-slate-100 text-sm mt-0.5 block truncate">
              {formatCoinAmount(lifetimeEarned, 0)} NOVA
            </span>
          </div>

          <div className="bg-black/25 rounded-2xl p-2.5 border border-white/5">
            <span className="text-gray-400 text-[10px] uppercase font-bold tracking-wider block">
              24h Daily Yield
            </span>
            <span className="font-mono font-black text-cyan-300 text-sm mt-0.5 block truncate">
              +{totalDailyRate.toLocaleString()} / 24h
            </span>
          </div>
        </div>
      </GlassCard>

      {/* 2. Central Mini Mining Reactor & Unclaimed Rewards Banner */}
      <GlassCard variant="cyan" className="border-cyan-500/35">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center space-x-4 w-full sm:w-auto">
            {/* Compact Animated Central Reactor */}
            <div className="flex-shrink-0">
              <MiningReactor
                size={82}
                progressPercent={cyclePercent}
                variant="cyan"
                status={canClaim ? 'mining' : 'idle'}
              />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center space-x-1.5">
                <span className="text-[11px] font-bold uppercase tracking-wider text-cyan-200">
                  Unclaimed Mined Coins
                </span>
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-ping" />
              </div>

              <div className="flex items-baseline space-x-1.5 mt-0.5">
                <AnimatedCoinCounter
                  value={liveAccrued}
                  ratePerSecond={totalRatePerSec}
                  decimals={4}
                  className="text-xl sm:text-2xl font-black font-mono text-cyan-gradient"
                />
                <span className="text-xs font-black text-cyan-300 font-mono">NOVA</span>
              </div>

              <div className="text-[10px] text-gray-400 mt-0.5 flex items-center space-x-1">
                <span>Cycle: {cyclePercent}%</span>
                <span>•</span>
                <span className="font-mono text-cyan-300">+{totalRatePerSec.toFixed(4)}/s</span>
              </div>
            </div>
          </div>

          {/* Quick One-Click Claim */}
          <div className="w-full sm:w-auto">
            <AnimatedButton
              variant="cyan"
              size="md"
              onClick={onClaimAll}
              disabled={!canClaim || isClaiming}
              isLoading={isClaiming}
              icon={<ArrowDownToLine className="w-4 h-4" />}
              className="w-full sm:w-auto justify-center"
            >
              One-Click Claim
            </AnimatedButton>
          </div>
        </div>
      </GlassCard>

      {/* 3. VIP Stars Miner Promotion Feature Card */}
      <GradientCard gradientTheme="gold" onClick={() => onNavigate('stars')} className="cursor-pointer">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-2xl bg-amber-500/20 border border-amber-400/40 flex items-center justify-center text-nova-gold shadow-lg shadow-amber-500/20 flex-shrink-0">
              <Star className="w-6 h-6 fill-current text-nova-gold animate-pulse" />
            </div>

            <div>
              <div className="flex items-center space-x-2">
                <span className="text-sm font-black text-white">VIP Stars Miner Fleet</span>
                <span className="px-2 py-0.5 rounded-full bg-amber-400/20 text-nova-gold text-[10px] font-black border border-amber-400/40">
                  10 ⭐
                </span>
              </div>
              <p className="text-xs text-amber-200/80 mt-0.5">
                Earn <span className="font-mono font-bold text-white">+100,000 coins / 24h</span> per miner
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-1 text-nova-gold text-xs font-bold pl-2">
            <span className="hidden sm:inline">Inspect</span>
            <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </GradientCard>

      {/* 4. Mining Rigs Fleet Status Breakdown */}
      <div className="space-y-3">
        <div className="flex items-center justify-between px-1">
          <h3 className="text-xs font-black uppercase tracking-wider text-gray-300 flex items-center space-x-2">
            <Activity className="w-3.5 h-3.5 text-cyan-400" />
            <span>Active Miner Fleet</span>
            <span className="text-white font-mono bg-white/10 px-2 py-0.5 rounded-md text-[10px]">
              {totalMinersCount} Online
            </span>
          </h3>

          <button
            onClick={() => onNavigate('main')}
            className="text-xs font-bold text-cyan-400 hover:text-cyan-300 transition-colors flex items-center space-x-1"
          >
            <span>Manage All</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Main Rig Card */}
          <GlassCard
            variant="purple"
            onClick={() => onNavigate('main')}
            className="cursor-pointer hover:border-purple-400/50 transition-all p-4"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-purple-500/20 border border-purple-400/30 flex items-center justify-center text-purple-300">
                  <Cpu className="w-5 h-5 text-purple-300" />
                </div>
                <div>
                  <h4 className="text-xs font-extrabold text-white">Main Core Miner</h4>
                  <span className="text-[11px] font-mono font-bold text-purple-300">
                    2,000 / 24h
                  </span>
                </div>
              </div>

              <MinerStatusBadge status={mainActive ? 'active' : 'paused'} />
            </div>

            <div className="mt-3 pt-2.5 border-t border-white/10 flex items-center justify-between text-[11px]">
              <span className="text-gray-400">Yield:</span>
              <span className="font-mono font-bold text-slate-200">
                {(2000 / 86400).toFixed(4)} / sec
              </span>
            </div>
          </GlassCard>

          {/* Auto Miner Card */}
          <GlassCard
            variant="cyan"
            onClick={() => onNavigate('auto')}
            className="cursor-pointer hover:border-cyan-400/50 transition-all p-4"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-cyan-500/20 border border-cyan-400/30 flex items-center justify-center text-cyan-300">
                  <Bot className="w-5 h-5 text-cyan-300" />
                </div>
                <div>
                  <h4 className="text-xs font-extrabold text-white">Autonomous Rig</h4>
                  <span className="text-[11px] font-mono font-bold text-cyan-300">
                    2,000 / 24h
                  </span>
                </div>
              </div>

              <MinerStatusBadge status={autoUnlocked ? 'active' : 'locked'} />
            </div>

            <div className="mt-3 pt-2.5 border-t border-white/10 flex items-center justify-between text-[11px]">
              <span className="text-gray-400">Requirement:</span>
              <span className="font-bold text-slate-200">
                {autoUnlocked ? 'Fully Active' : 'Complete 3 Tasks'}
              </span>
            </div>
          </GlassCard>
        </div>
      </div>

      {/* 5. Referrals & Rewards Quick Action Card */}
      <GlassCard
        variant="magenta"
        onClick={() => onNavigate('referrals')}
        className="cursor-pointer hover:border-pink-400/50 transition-all"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-11 h-11 rounded-2xl bg-pink-500/20 border border-pink-400/35 flex items-center justify-center text-pink-300 shadow-md">
              <Award className="w-5 h-5 text-pink-300" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h4 className="text-xs font-black text-white">Referral Network Bounty</h4>
                <span className="text-[10px] font-mono font-extrabold px-1.5 py-0.5 rounded bg-pink-500/20 text-pink-300 border border-pink-500/30">
                  +10,000 NOVA
                </span>
              </div>
              <p className="text-[11px] text-gray-300 mt-0.5">
                Invited Friends: <span className="font-mono font-bold text-white">{profile?.referrals.count || 0}</span> • Total Bonus:{' '}
                <span className="font-mono font-bold text-nova-gold">{formatCoinAmount(profile?.referrals.total_reward || 0, 0)}</span>
              </p>
            </div>
          </div>

          <ArrowRight className="w-4 h-4 text-pink-300" />
        </div>
      </GlassCard>
    </div>
  );
};
